// src/pages/PromotionCalculatorPage.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  collection,
  collectionGroup,
  query,
  orderBy,
  getDocs,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/WorkHoursManagementPage.module.css'; // reuse existing styles

const managerRoles = ['Manager', 'Accounts Manager'];
const ILS = (n) => new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(Number(n) || 0);

// ✅ Use a SAFE doc id (no double underscores)
const GLOBAL_PLAN_ID = 'ALL_CLIENTS';

// ---- Date helpers (UTC-based) ----
const toUTCDate = (yyyy_mm_dd) => new Date(`${yyyy_mm_dd}T00:00:00Z`);
const todayUTCString = () => {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const diffDaysInclusive = (startStr, endStr) => {
  const s = toUTCDate(startStr);
  const e = toUTCDate(endStr);
  if (isNaN(s) || isNaN(e)) return 0;
  return Math.floor((e - s) / 86400000) + 1;
};
const clampDateStr = (dateStr, minStr, maxStr) => {
  const d = toUTCDate(dateStr);
  const min = toUTCDate(minStr);
  const max = toUTCDate(maxStr);
  if (d < min) return minStr;
  if (d > max) return maxStr;
  return dateStr;
};
const isValidRange = (start, end) => !!start && !!end && toUTCDate(start) <= toUTCDate(end);

// ---- Accrual logic (respects stored/paused) ----
const effectiveAccrualEnd = (promo, asOfStr) => {
  let cap = asOfStr;
  if (promo.endDate) cap = clampDateStr(promo.endDate, promo.startDate, cap);
  if (promo.isStored && promo.storeDate) cap = clampDateStr(promo.storeDate, promo.startDate, cap);
  return cap;
};

const accruePromotionToDate = (promo, asOfStr) => {
  const type = promo.type || 'fixed';
  const start = promo.startDate;
  if (!start) return 0;

  if (toUTCDate(asOfStr) < toUTCDate(start)) return 0;

  if (type === 'daily') {
    const perDay = Number(promo.perDayAmount);
    if (!perDay || perDay <= 0) return 0;
    const endCap = effectiveAccrualEnd(promo, asOfStr) || asOfStr;
    if (toUTCDate(endCap) < toUTCDate(start)) return 0;
    const elapsedDays = diffDaysInclusive(start, endCap);
    return Math.max(0, elapsedDays) * perDay;
  }

  // fixed (spread across period)
  const total = Number(promo.amount);
  const end = promo.endDate;
  if (!total || total <= 0 || !end) return 0;

  const totalDays = diffDaysInclusive(start, end);
  if (totalDays <= 0) return 0;

  const endCap = effectiveAccrualEnd(promo, asOfStr);
  if (!endCap || toUTCDate(endCap) < toUTCDate(start)) return 0;

  const elapsedDays = diffDaysInclusive(start, endCap);
  const perDay = total / totalDays;
  return Math.min(total, Math.max(0, elapsedDays) * perDay);
};

const PromotionCalculatorPage = () => {
  const { userRole, currentUser } = useAuth();

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');

  const [promotions, setPromotions] = useState([]);
  const [payments, setPayments] = useState([]); // per-client payments (legacy)

  // GLOBAL payments (new requirement)
  const [globalPayments, setGlobalPayments] = useState([]);

  // all-clients aggregation (collectionGroup)
  const [allPromotions, setAllPromotions] = useState([]);
  const [allPayments, setAllPayments] = useState([]);

  // Add Promotion form
  const [promoType, setPromoType] = useState('fixed'); // 'fixed' | 'daily'
  const [promoAmount, setPromoAmount] = useState('');
  const [promoPerDay, setPromoPerDay] = useState('');
  const [promoStart, setPromoStart] = useState('');
  const [promoEnd, setPromoEnd] = useState('');
  const [promoNote, setPromoNote] = useState('');

  // Add Payment (GLOBAL) form
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payNote, setPayNote] = useState('');

  // Edit Promotion modal
  const [editPromo, setEditPromo] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editPerDay, setEditPerDay] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editNote, setEditNote] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  // Load clients
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(query(collection(db, 'clients'), orderBy('name', 'asc')));
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        if (!isMounted.current) return;
        setClients(list);
        if (list.length && !clientId) {
          setClientId(list[0].id);
          setClientName(list[0].name || '');
        }
      } catch (e) {
        console.error(e);
        if (isMounted.current) setErr('Failed to load clients.');
      } finally {
        if (isMounted.current) setLoading(false);
      }
    })();
  }, []); // once

  // Ensure GLOBAL plan exists (used for global payments)
  const ensureGlobalPlan = useCallback(async () => {
    try {
      await setDoc(
        doc(db, 'promotionPlans', GLOBAL_PLAN_ID),
        {
          clientId: GLOBAL_PLAN_ID,
          clientName: 'ALL',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('Could not ensure global plan:', e);
    }
  }, []);

  // Listen to selected client's promotions & (legacy) payments
  useEffect(() => {
    if (!clientId) {
      setPromotions([]);
      setPayments([]);
      return;
    }

    // Ensure plan doc exists for selected client
    (async () => {
      try {
        await setDoc(
          doc(db, 'promotionPlans', clientId),
          {
            clientId,
            clientName: clientName || (clients.find(c => c.id === clientId)?.name ?? ''),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn('Could not ensure promotion plan doc exists:', e);
      }
    })();

    const promosRef = collection(db, 'promotionPlans', clientId, 'promotions');
    const paysRef = collection(db, 'promotionPlans', clientId, 'payments'); // legacy/client-tied payments

    const unsubPromos = onSnapshot(
      query(promosRef, orderBy('startDate', 'asc')),
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        if (isMounted.current) setPromotions(rows);
      },
      (e) => {
        console.error(e);
        if (isMounted.current) setErr('Failed to load promotions.');
      }
    );

    const unsubPays = onSnapshot(
      query(paysRef, orderBy('date', 'asc')),
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        if (isMounted.current) setPayments(rows);
      },
      (e) => {
        console.error(e);
        if (isMounted.current) setErr('Failed to load client payments.');
      }
    );

    return () => {
      unsubPromos();
      unsubPays();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // GLOBAL payments listener
  useEffect(() => {
    ensureGlobalPlan();
    const unsub = onSnapshot(
      query(collection(db, 'promotionPlans', GLOBAL_PLAN_ID, 'payments'), orderBy('date', 'desc')),
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        if (isMounted.current) setGlobalPayments(rows);
      },
      (e) => console.error('Global payments listener failed:', e)
    );
    return () => unsub();
  }, [ensureGlobalPlan]);

  // All-clients listeners (sums both client-tied and global payments)
  useEffect(() => {
    const unsubAllPromos = onSnapshot(
      query(collectionGroup(db, 'promotions')),
      (snap) => {
        const rows = snap.docs.map(d => d.data() || {});
        if (isMounted.current) setAllPromotions(rows);
      },
      (e) => console.error('All-promotions listener failed:', e)
    );

    const unsubAllPays = onSnapshot(
      query(collectionGroup(db, 'payments')),
      (snap) => {
        const rows = snap.docs.map(d => d.data() || {});
        if (isMounted.current) setAllPayments(rows);
      },
      (e) => console.error('All-payments listener failed:', e)
    );

    return () => { unsubAllPromos(); unsubAllPays(); };
  }, []);

  const asOf = todayUTCString();

  // Selected client totals (GLOBAL payments are excluded by design)
  const totals = useMemo(() => {
    const accruedPromotions = promotions.reduce((sum, p) => sum + accruePromotionToDate(p, asOf), 0);
    const totalPayments = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0); // client-only
    const balance = accruedPromotions - totalPayments;
    return { accruedPromotions, totalPayments, balance, asOf };
  }, [promotions, payments, asOf]);

  // All clients totals (includes GLOBAL + any client-tied payments)
  const totalsAll = useMemo(() => {
    const accruedPromotions = allPromotions.reduce((sum, p) => sum + accruePromotionToDate(p, asOf), 0);
    const totalPayments = allPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const balance = accruedPromotions - totalPayments;
    return { accruedPromotions, totalPayments, balance, asOf };
  }, [allPromotions, allPayments, asOf]);

  // Mirror selected client's totals
  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        await updateDoc(doc(db, 'promotionPlans', clientId), {
          totals: {
            accruedPromotions: Number(totals.accruedPromotions) || 0,
            totalPayments: Number(totals.totalPayments) || 0,
            balance: Number(totals.balance) || 0,
            asOf: totals.asOf,
          },
          updatedAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'clients', clientId), {
          promotionTotals: {
            accruedPromotions: Number(totals.accruedPromotions) || 0,
            totalPayments: Number(totals.totalPayments) || 0,
            asOf: totals.asOf,
          },
          promotionBalance: Number(totals.balance) || 0,
          promotionUpdatedAt: serverTimestamp(),
        }).catch(() => {});
      } catch (e) {
        console.warn('Could not update mirrored totals:', e);
      }
    })();
  }, [clientId, totals]);

  const onChangeClient = (e) => {
    const id = e.target.value;
    const c = clients.find(x => x.id === id);
    setClientId(id);
    setClientName(c?.name || '');
    setMsg('');
    setErr('');
  };

  // --- Add Promotion ---
  const addPromotion = async () => {
    setMsg(''); setErr('');
    if (!clientId) return setErr('Select a client.');

    if (promoType === 'fixed') {
      const amount = Number(promoAmount);
      if (!amount || amount <= 0) return setErr('Enter a valid total amount.');
      if (!promoStart || !promoEnd || !isValidRange(promoStart, promoEnd)) {
        return setErr('Enter a valid date range (start <= end).');
      }
      setBusy(true);
      try {
        await addDoc(collection(db, 'promotionPlans', clientId, 'promotions'), {
          type: 'fixed',
          amount,
          startDate: promoStart,
          endDate: promoEnd,
          note: (promoNote || '').trim(),
          isStored: false,
          storeDate: null,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.uid || 'system',
        });
        setPromoAmount(''); setPromoStart(''); setPromoEnd(''); setPromoNote('');
        setMsg('✅ Fixed promotion added.');
      } catch (e) {
        console.error(e);
        setErr('Failed to add promotion.');
      } finally {
        setBusy(false);
      }
      return;
    }

    // daily
    const perDay = Number(promoPerDay);
    if (!perDay || perDay <= 0) return setErr('Enter a valid per-day amount.');
    if (!promoStart) return setErr('Start date is required.');
    if (promoEnd && !isValidRange(promoStart, promoEnd)) {
      return setErr('End date must be after or equal to start date.');
    }

    setBusy(true);
    try {
      await addDoc(collection(db, 'promotionPlans', clientId, 'promotions'), {
        type: 'daily',
        perDayAmount: perDay,
        startDate: promoStart,
        endDate: promoEnd || null,
        note: (promoNote || '').trim(),
        isStored: false,
        storeDate: null,
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || 'system',
      });
      setPromoPerDay(''); setPromoStart(''); setPromoEnd(''); setPromoNote('');
      setMsg('✅ Daily promotion added.');
    } catch (e) {
      console.error(e);
      setErr('Failed to add promotion.');
    } finally {
      setBusy(false);
    }
  };

  // --- Add Payment (GLOBAL ONLY) ---
  const addPayment = async () => {
    setMsg(''); setErr('');
    const amount = Number(payAmount);
    if (!amount || amount <= 0) return setErr('Enter a valid payment amount.');
    if (!payDate) return setErr('Payment date is required.');

    setBusy(true);
    try {
      await ensureGlobalPlan();
      await addDoc(collection(db, 'promotionPlans', GLOBAL_PLAN_ID, 'payments'), {
        amount,
        date: payDate,
        note: (payNote || '').trim(),
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || 'system',
      });
      setPayAmount(''); setPayDate(''); setPayNote('');
      setMsg('✅ Global payment added (affects All Clients totals).');
    } catch (e) {
      console.error(e);
      setErr('Failed to add global payment.');
    } finally {
      setBusy(false);
    }
  };

  // --- Delete Promotion / Payment ---
  const removePromotion = useCallback(async (id) => {
    if (!clientId || !id) return;
    if (!window.confirm('Delete this promotion entry?')) return;
    try {
      await deleteDoc(doc(db, 'promotionPlans', clientId, 'promotions', id));
      setMsg('🗑️ Promotion deleted.');
    } catch (e) {
      console.error(e);
      setErr('Failed to delete promotion.');
    }
  }, [clientId]);

  const removePayment = useCallback(async (id) => {
    if (!clientId || !id) return;
    if (!window.confirm('Delete this client payment?')) return;
    try {
      await deleteDoc(doc(db, 'promotionPlans', clientId, 'payments', id));
      setMsg('🗑️ Client payment deleted.');
    } catch (e) {
      console.error(e);
      setErr('Failed to delete client payment.');
    }
  }, [clientId]);

  const removeGlobalPayment = useCallback(async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this GLOBAL payment?')) return;
    try {
      await deleteDoc(doc(db, 'promotionPlans', GLOBAL_PLAN_ID, 'payments', id));
      setMsg('🗑️ Global payment deleted.');
    } catch (e) {
      console.error(e);
      setErr('Failed to delete global payment.');
    }
  }, []);

  // --- Store / Resume Promotion ---
  const toggleStorePromotion = async (promo) => {
    setErr(''); setMsg('');
    if (!clientId || !promo?.id) return;

    if (!promo.isStored) {
      const defaultDate = todayUTCString();
      const stopDate = window.prompt('Choose stop (store) date (YYYY-MM-DD):', defaultDate);
      if (!stopDate) return;
      if (!promo.startDate || toUTCDate(stopDate) < toUTCDate(promo.startDate)) {
        setErr('Stop date must be on/after promotion start.');
        return;
      }
      if (promo.endDate && toUTCDate(stopDate) > toUTCDate(promo.endDate)) {
        setErr('Stop date cannot exceed end date.');
        return;
      }
      try {
        await updateDoc(doc(db, 'promotionPlans', clientId, 'promotions', promo.id), {
          isStored: true,
          storeDate: stopDate,
          status: 'stored',
          editedAt: serverTimestamp(),
          editedBy: currentUser?.uid || 'system',
        });
        setMsg('⏸️ Promotion stored (accrual stopped).');
      } catch (e) {
        console.error(e);
        setErr('Failed to store promotion.');
      }
    } else {
      if (!window.confirm('Resume this promotion (accrual will continue)?')) return;
      try {
        await updateDoc(doc(db, 'promotionPlans', clientId, 'promotions', promo.id), {
          isStored: false,
          storeDate: null,
          status: 'active',
          editedAt: serverTimestamp(),
          editedBy: currentUser?.uid || 'system',
        });
        setMsg('▶️ Promotion resumed.');
      } catch (e) {
        console.error(e);
        setErr('Failed to resume promotion.');
      }
    }
  };

  // --- Edit Promotion ---
  const openEditPromo = (p) => {
    setEditPromo(p);
    setEditAmount(p.amount ?? '');
    setEditPerDay(p.perDayAmount ?? '');
    setEditStart(p.startDate ?? '');
    setEditEnd(p.endDate ?? '');
    setEditNote(p.note ?? '');
  };
  const saveEditPromo = async () => {
    if (!clientId || !editPromo?.id) return;
    const payload = {
      startDate: editStart || null,
      endDate: editEnd || null,
      note: (editNote || '').trim(),
      editedAt: serverTimestamp(),
      editedBy: currentUser?.uid || 'system',
    };
    if ((editPromo.type || 'fixed') === 'fixed') {
      const amount = Number(editAmount);
      if (!amount || amount <= 0) { setErr('Enter a valid total amount.'); return; }
      if (!editStart || !editEnd || !isValidRange(editStart, editEnd)) {
        setErr('Enter a valid date range (start <= end).'); return;
      }
      payload.amount = amount;
    } else {
      const perDay = Number(editPerDay);
      if (!perDay || perDay <= 0) { setErr('Enter a valid per-day amount.'); return; }
      if (!editStart) { setErr('Start date is required.'); return; }
      if (editEnd && !isValidRange(editStart, editEnd)) {
        setErr('End date must be on/after start date.'); return;
      }
      payload.perDayAmount = perDay;
    }
    try {
      await updateDoc(doc(db, 'promotionPlans', clientId, 'promotions', editPromo.id), payload);
      setMsg('✏️ Promotion updated.');
      setEditPromo(null);
    } catch (e) {
      console.error(e);
      setErr('Failed to update promotion.');
    }
  };

  if (!managerRoles.includes(userRole)) {
    return <div className={styles.container}>Access denied.</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h2>Promotion Calculator & Balance</h2>
        <div className={styles.controls}>
          <label className={styles.control}>
            <span>Client</span>
            <select value={clientId} onChange={onChangeClient} disabled={loading}>
              {clients.length === 0 && <option value="">— No clients —</option>}
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {(err || msg) && (
        <div className={styles.info} style={{ marginBottom: 12 }}>
          {err ? <span style={{ color: '#b00020' }}>{err}</span> : <span>{msg}</span>}
        </div>
      )}

      {/* Totals */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Totals</h3>
        <div className={styles.formGrid}>
          <div className={styles.control}>
            <span>Selected Client — Total Promotions (accrued as of {totals.asOf})</span>
            <div><strong>{ILS(totals.accruedPromotions)}</strong></div>
          </div>
          <div className={styles.control}>
            <span>Selected Client — Total Payments</span>
            <div><strong>{ILS(totals.totalPayments)}</strong></div>
          </div>
          <div className={styles.control}>
            <span>Selected Client — Balance</span>
            <div><strong>{ILS(totals.balance)}</strong></div>
          </div>

          <div className={styles.control} style={{ marginTop: 12, gridColumn: '1 / -1' }}>
            <hr style={{ opacity: 0.2, margin: '8px 0' }} />
          </div>

          <div className={styles.control}>
            <span>All Clients — Total Promotions (accrued as of {totalsAll.asOf})</span>
            <div><strong>{ILS(totalsAll.accruedPromotions)}</strong></div>
          </div>
          <div className={styles.control}>
            <span>All Clients — Total Payments</span>
            <div><strong>{ILS(totalsAll.totalPayments)}</strong></div>
          </div>
          <div className={styles.control}>
            <span>All Clients — Balance</span>
            <div><strong>{ILS(totalsAll.balance)}</strong></div>
          </div>
        </div>
        <p className={styles.note}>
          Fixed promotions accrue evenly across their date range. Daily promotions add a fixed amount for each day in range (or until today if no end date). <br />
          <strong>Stored</strong> promotions stop accruing from the selected store date.
        </p>
      </div>

      {/* Add Promotion */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Add Promotion</h3>

        <div className={styles.formGrid}>
          <label className={styles.control}>
            <span>Type</span>
            <select value={promoType} onChange={(e) => setPromoType(e.target.value)}>
              <option value="fixed">Fixed (spread over period)</option>
              <option value="daily">Daily (per day)</option>
            </select>
          </label>

          {promoType === 'fixed' ? (
            <>
              <label className={styles.control}>
                <span>Total Amount (ILS)</span>
                <input type="number" min="0" step="any" value={promoAmount} onChange={(e) => setPromoAmount(e.target.value)} placeholder="0.00" />
              </label>
              <label className={styles.control}>
                <span>Start Date</span>
                <input type="date" value={promoStart} onChange={(e) => setPromoStart(e.target.value)} />
              </label>
              <label className={styles.control}>
                <span>End Date</span>
                <input type="date" value={promoEnd} onChange={(e) => setPromoEnd(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className={styles.control}>
                <span>Per-day Amount (ILS)</span>
                <input type="number" min="0" step="any" value={promoPerDay} onChange={(e) => setPromoPerDay(e.target.value)} placeholder="e.g., 20" />
              </label>
              <label className={styles.control}>
                <span>Start Date</span>
                <input type="date" value={promoStart} onChange={(e) => setPromoStart(e.target.value)} />
              </label>
              <label className={styles.control}>
                <span>End Date (optional)</span>
                <input type="date" value={promoEnd} onChange={(e) => setPromoEnd(e.target.value)} />
              </label>
            </>
          )}

          <label className={styles.control} style={{ gridColumn: '1 / -1' }}>
            <span>Note (optional)</span>
            <input type="text" value={promoNote} onChange={(e) => setPromoNote(e.target.value)} placeholder="Describe this promotion…" />
          </label>
        </div>

        <div className={styles.actions}>
          <button className={styles.primaryBtn} onClick={addPromotion} disabled={busy || !clientId}>
            {busy ? 'Saving…' : 'Add Promotion'}
          </button>
        </div>
      </div>

      {/* Add GLOBAL Payment */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Add Payment (GLOBAL)</h3>
        <div className={styles.formGrid}>
          <label className={styles.control}>
            <span>Amount (ILS)</span>
            <input type="number" min="0" step="any" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" />
          </label>
          <label className={styles.control}>
            <span>Date</span>
            <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </label>
          <label className={styles.control} style={{ gridColumn: '1 / -1' }}>
            <span>Note (optional)</span>
            <input type="text" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Reference / bank transfer / comment…" />
          </label>
        </div>
        <div className={styles.actions}>
          <button className={styles.primaryBtn} onClick={addPayment} disabled={busy}>
            {busy ? 'Saving…' : 'Add Global Payment'}
          </button>
        </div>
        <p className={styles.note}>
          Global payments **do not** apply to a single client. They are counted only in <strong>Totals → All Clients</strong>.
        </p>
      </div>

      {/* Promotions list */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Promotion Entries</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Configured Amount</th>
                <th>Accrued (as of {asOf})</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promotions.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 12 }}>No promotions yet.</td></tr>
              ) : promotions.map(p => {
                const accrued = accruePromotionToDate(p, asOf);
                const isDaily = (p.type || 'fixed') === 'daily';
                const statusTxt = p.isStored ? `Stored ${p.storeDate ? `(${p.storeDate})` : ''}` : 'Active';
                return (
                  <tr key={p.id}>
                    <td>{isDaily ? 'Daily' : 'Fixed'}</td>
                    <td>
                      {isDaily
                        ? <>Per-day: <strong>{ILS(p.perDayAmount)}</strong></>
                        : <><strong>{ILS(p.amount)}</strong> (spread)</>
                      }
                    </td>
                    <td>{ILS(accrued)}</td>
                    <td>{p.startDate || '—'}</td>
                    <td>{p.endDate || (isDaily ? '— (ongoing)' : '—')}</td>
                    <td>{statusTxt}</td>
                    <td>{p.note || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className={styles.smallBtn} onClick={() => openEditPromo(p)}>Edit</button>{' '}
                      <button className={styles.smallBtn} onClick={() => toggleStorePromotion(p)}>
                        {p.isStored ? 'Resume' : 'Store'}
                      </button>{' '}
                      <button className={styles.smallBtn} onClick={() => removePromotion(p.id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className={styles.note}>
          “Store” freezes accrual from the chosen date. “Resume” continues accrual again.
        </p>
      </div>

      {/* Payments list (client-tied, shown for legacy/reference) */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Payments (Client-only)</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Amount</th>
                <th>Date</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 12 }}>No client payments.</td></tr>
              ) : payments.map(p => (
                <tr key={p.id}>
                  <td>{ILS(p.amount)}</td>
                  <td>{p.date || '—'}</td>
                  <td>{p.note || '—'}</td>
                  <td>
                    <button className={styles.smallBtn} onClick={() => removePayment(p.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.note}>
          New payments you add from this page are GLOBAL and won’t appear here.
        </p>
      </div>

      {/* GLOBAL Payments list */}
      <div className={styles.card}>
        <h3 className={styles.subheading}>Global Payments (Affect All Clients)</h3>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Amount</th>
                <th>Date</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {globalPayments.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 12 }}>No global payments yet.</td></tr>
              ) : globalPayments.map(p => (
                <tr key={p.id}>
                  <td>{ILS(p.amount)}</td>
                  <td>{p.date || '—'}</td>
                  <td>{p.note || '—'}</td>
                  <td>
                    <button className={styles.smallBtn} onClick={() => removeGlobalPayment(p.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.note}>
          These payments are stored under <code>promotionPlans/{GLOBAL_PLAN_ID}/payments</code> and included in <strong>All Clients</strong> totals.
        </p>
      </div>

      {/* Edit Promotion Modal */}
      {editPromo && (
        <div className={styles.modalBackdrop} onClick={() => setEditPromo(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Edit Promotion</h3>
            <p className={styles.muted}>
              Type: <strong>{(editPromo.type || 'fixed').toUpperCase()}</strong>
              {editPromo.isStored && <> • Status: <strong>Stored{editPromo.storeDate ? ` (${editPromo.storeDate})` : ''}</strong></>}
            </p>

            {(editPromo.type || 'fixed') === 'fixed' ? (
              <>
                <div className={styles.formRow}>
                  <label>Total Amount (ILS)</label>
                  <input type="number" min="0" step="any" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} />
                </div>
                <div className={styles.formRow}>
                  <label>Start Date</label>
                  <input type="date" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
                </div>
                <div className={styles.formRow}>
                  <label>End Date</label>
                  <input type="date" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <div className={styles.formRow}>
                  <label>Per-day Amount (ILS)</label>
                  <input type="number" min="0" step="any" value={editPerDay} onChange={(e) => setEditPerDay(e.target.value)} />
                </div>
                <div className={styles.formRow}>
                  <label>Start Date</label>
                  <input type="date" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
                </div>
                <div className={styles.formRow}>
                  <label>End Date (optional)</label>
                  <input type="date" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
                </div>
              </>
            )}

            <div className={styles.formRow}>
              <label>Note (optional)</label>
              <input type="text" value={editNote} onChange={(e) => setEditNote(e.target.value)} />
            </div>

            <div className={styles.actions}>
              <button className={styles.secondaryBtn} onClick={() => setEditPromo(null)}>Cancel</button>
              <button className={styles.primaryBtn} onClick={saveEditPromo}>Save</button>
            </div>

            <p className={styles.note}>
              Saving will immediately affect accrued totals.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionCalculatorPage;
